/**
 * Canonical source of truth for event lock-in columns on the frontend.
 *
 * ⚠️ MUST STAY IN SYNC with api/src/events/event-locks.js. The monorepo merge did
 * not consolidate these two files: both still exist, and a lock added to one and
 * not the other is a half-working ring. Edit them together.
 *
 * Adding a new lock:
 *   1. Add an entry here
 *   2. Mirror it in api/src/events/event-locks.js
 *   3. Add the DB column in a new postgrator migration under api/migrations/
 *
 * Field descriptor forms:
 *   'event_date'              → top-level event column
 *   { detail: 'load_in' }     → details_json sub-key
 *   { special: 'crew' }       → crew count (computed separately)
 *   { special: 'contacts' }   → contacts array (filled if length > 0)
 *   { field, conditional }    → field that's only counted when predicate is true
 */

export const EVENT_LOCKS = [
  {
    key: 'locked_main',
    tab: 'main',
    fields: [
      'name', 'event_date', 'show_time', 'notes',
      // second_show_time is intentionally NOT in the completion count — it's
      // informational-only. Mirror of the rationale in the backend event-locks.js.
      { detail: 'load_in' }, { detail: 'sound_check' }, { detail: 'four_corners' },
      { detail: 'perimeter_secured' }, { detail: 'perimeter_lifted' }, { detail: 'load_out' },
    ],
  },
  {
    key: 'locked_location',
    tab: 'location',
    fields: ['location', 'launch_latitude', 'show_height_ft', 'surface_type'],
  },
  {
    key: 'locked_creative',
    tab: 'creative',
    fields: [
      // Array column: isFilled() counts it as filled at length > 0, so "has at
      // least one preview link" scores exactly what the single column used to.
      'preview_links',
      { detail: 'music' }, { detail: 'time_machine' }, { detail: 'music_link_code' }, { detail: 'creative' },
    ],
  },
  {
    key: 'locked_contacts',
    tab: 'contacts',
    fields: [
      { special: 'contacts' },
      { detail: 'onsite_contact_name' }, { detail: 'onsite_contact_phone' },
    ],
  },
  { key: 'locked_logistics_crew',          tab: 'logistics', fields: [{ special: 'crew' }] },
  { key: 'locked_logistics_flights',       tab: 'logistics', fields: [{ detail: 'flight' }] },
  { key: 'locked_logistics_rental_car',    tab: 'logistics', fields: [{ detail: 'rental_car' }] },
  { key: 'locked_logistics_box_truck',     tab: 'logistics', fields: [{ detail: 'box_truck' }] },
  { key: 'locked_logistics_hotel',         tab: 'logistics', fields: [{ detail: 'hotel' }] },
  { key: 'locked_logistics_packing',       tab: 'logistics', fields: [{ detail: 'packing' }] },
  {
    key: 'locked_logistics_transport',
    tab: 'logistics',
    fields: [
      'ship_out_date', 'ship_back_date',
      { field: 'tracking_number_out',    conditional: (form) => form.transport_mode !== 'driving' },
      { field: 'tracking_number_return', conditional: (form) => form.transport_mode !== 'driving' },
      { detail: 'gear_transportation' },
    ],
  },
];

export const EVENT_LOCK_KEYS = EVENT_LOCKS.map(l => l.key);

/**
 * Should this field descriptor be counted for the given form state?
 * Call this BEFORE resolveFormValue so "excluded by conditional" doesn't
 * get confused with "value happens to be empty."
 */
export const isFormFieldIncluded = (fieldDesc, form) => {
  if (typeof fieldDesc === 'string') return true;
  if (!fieldDesc.conditional) return true;
  return !!fieldDesc.conditional(form);
};

/**
 * Resolve a field descriptor to its current value on the form state.
 * `crewCount` is separate because it lives outside form state in EventForm.
 * `contactsArr` is form.contacts (already an array).
 */
export const resolveFormValue = (fieldDesc, form, crewCount, contactsArr) => {
  if (typeof fieldDesc === 'string') return form[fieldDesc];
  if (fieldDesc.detail) return form.details_json?.[fieldDesc.detail];
  if (fieldDesc.special === 'crew') return crewCount > 0 ? crewCount : null;
  if (fieldDesc.special === 'contacts') return contactsArr;
  return form[fieldDesc.field];
};

/** Default lock flags for a fresh form (all false). */
export const defaultLockFlags = () =>
  Object.fromEntries(EVENT_LOCK_KEYS.map(k => [k, false]));

/** Hydrate lock flags from a server event response (coerce to booleans). */
export const hydrateLockFlags = (event) =>
  Object.fromEntries(EVENT_LOCK_KEYS.map(k => [k, !!event[k]]));

/** Pull lock flags out of form state for PATCH body (coerce to booleans). */
export const extractLockFlags = (form) =>
  Object.fromEntries(EVENT_LOCK_KEYS.map(k => [k, !!form[k]]));
